import i18next, { type i18n } from "i18next";
import ICU from "i18next-icu";
import { initReactI18next } from "react-i18next";

import { applyDocumentLanguage, fallbackLanguage, type Language } from "./languages";
import { defaultNamespace, namespaces, resources } from "./resources";

export interface CreateI18nOptions {
  language: Language;
  /** Throw on missing keys (tests) so untranslated keys never ship (ADR-0018/D-6). */
  strict?: boolean;
}

/** Creates and initialises an i18next instance with ICU MessageFormat and the bundled resources. */
export async function createI18n({ language, strict = false }: CreateI18nOptions): Promise<i18n> {
  const instance = i18next.createInstance();
  await instance
    .use(ICU)
    .use(initReactI18next)
    .init({
      lng: language,
      fallbackLng: fallbackLanguage,
      supportedLngs: ["ar", "en"],
      ns: namespaces,
      defaultNS: defaultNamespace,
      resources,
      interpolation: { escapeValue: false },
      returnNull: false,
      saveMissing: strict,
      missingKeyHandler: strict
        ? (_lngs, ns, key) => {
            throw new Error(`Missing translation: ${ns}:${key}`);
          }
        : false,
    });
  applyDocumentLanguage(language);
  return instance;
}

/**
 * Switches language at runtime without a reload (ADR-0009/D-1): i18next re-renders bound components and the
 * document direction flips. UI5 components are switched by the UI package (see `@positive/ui`).
 */
export async function changeLanguage(instance: i18n, language: Language): Promise<void> {
  await instance.changeLanguage(language);
  applyDocumentLanguage(language);
}
